import { memo, useCallback, useState } from 'react';
import { IconHighlight, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Ban } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';
import MembersSuspendStep1 from './MembersSuspendStep1';
import MembersSuspendStep2 from './MembersSuspendStep2';

const TITLE_ID = 'members-suspend-title';
const STEP = Object.freeze({ REVIEW: 1, CONFIRM: 2 });

/**
 * The open bulk Suspend dialog and its two steps. Mount it only while there is something to
 * suspend, so the step and the typed word start fresh each time.
 *
 * @param {object} props
 * @param {{ id: string, displayName: string, email: string }[]} props.members
 * @param {boolean} props.isBusy Suspending: Back, the way out and the confirm button are locked.
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const MembersSuspendFlow = ({ members, isBusy, onConfirm, onCancel }) => {
  const { t } = useTranslation('people-access');
  const [step, setStep] = useState(STEP.REVIEW);
  const [typed, setTyped] = useState('');
  const count = members.length;
  const word = t('members.suspend.confirmWord');
  // The word is typed exactly (SUSPEND), not in any case.
  const isMatch = typed.trim() === word;

  // Esc closes unless the suspension is in flight.
  const handleEscape = useCallback(() => {
    if (!isBusy) onCancel();
  }, [isBusy, onCancel]);
  useDialogKeyboard(true, handleEscape, TITLE_ID);

  const handleContinue = useCallback(() => setStep(STEP.CONFIRM), []);
  const handleBack = useCallback(() => setStep(STEP.REVIEW), []);

  return (
    <ModalLayout
      isOpen
      onClose={onCancel}
      useBodyPortal
      title={t('members.suspend.title', { count })}
      subtitle={step === STEP.REVIEW ? t('members.suspend.step1', { count }) : t('members.suspend.step2', { count, word })}
      closeAriaLabel={t('members.suspend.close')}
      titleId={TITLE_ID}
      titlePrefix={<IconHighlight icon={Ban} bgColor="Alert" iconColor="Alert" size="medium" />}
      panelClassName={NO_HEADER_LINE}
      closeDisabled={isBusy}
      maxWidthClassName={WIDE_MODAL}
    >
      {step === STEP.REVIEW ? (
        <MembersSuspendStep1 members={members} onCancel={onCancel} onContinue={handleContinue} />
      ) : (
        <MembersSuspendStep2
          word={word}
          count={count}
          value={typed}
          isMatch={isMatch}
          isBusy={isBusy}
          onChange={setTyped}
          onBack={handleBack}
          onConfirm={onConfirm}
        />
      )}
    </ModalLayout>
  );
};

export default memo(MembersSuspendFlow);
